<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';

const props = defineProps({
  numeroContrato: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();

const ordenes = ref([]);
const isLoading = ref(true);
const error = ref(null);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const PER_PAGE = 25;

const statusColors = {
  draft:          'bg-n-slate-9/10 text-n-slate-11',
  ready:          'bg-n-amber-9/10 text-n-amber-11',
  dispatched:     'bg-n-brand/10 text-n-blue-text',
  en_route:       'bg-violet-100 text-violet-800',
  on_site:        'bg-n-brand/10 text-n-blue-text',
  field_complete: 'bg-n-teal-9/10 text-n-teal-11',
  redirected:     'bg-orange-100 text-orange-800',
  cancelled:      'bg-n-ruby-9/10 text-n-ruby-11',
  merged:         'bg-n-slate-9/10 text-n-slate-11',
};

const statusLabel = status => {
  const map = {
    draft:          t('ORDERS.STATUS_DRAFT'),
    ready:          t('ORDERS.STATUS_READY'),
    dispatched:     t('ORDERS.STATUS_DISPATCHED'),
    en_route:       t('ORDERS.STATUS_EN_ROUTE'),
    on_site:        t('ORDERS.STATUS_ON_SITE'),
    field_complete: t('ORDERS.STATUS_FIELD_COMPLETE'),
    redirected:     t('ORDERS.STATUS_REDIRECTED'),
    cancelled:      t('ORDERS.STATUS_CANCELLED'),
    merged:         t('ORDERS.STATUS_MERGED'),
  };
  return map[status] || status;
};

const orderTypeLabel = orden => {
  if (orden.order_type_record) {
    return `${orden.order_type_record.code} - ${orden.order_type_record.description}`;
  }
  return orden.order_type || '-';
};

const orderClassLabel = orderClass =>
  orderClass === 'service_order' ? 'OS' : 'OT';

const formatDate = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const openOrder = orden => {
  router.push({ name: 'order_details', params: { orderId: orden.id } });
};

const fetchOrdenes = async (page = 1) => {
  isLoading.value = true;
  error.value = null;
  try {
    const res = await ServiceOrdersAPI.get({
      contract_number: props.numeroContrato,
      page,
      per_page: PER_PAGE,
    });
    ordenes.value = res.data?.payload ?? [];
    currentPage.value = res.data?.meta?.current_page ?? page;
    totalPages.value  = res.data?.meta?.total_pages ?? 1;
    totalCount.value  = res.data?.meta?.count ?? ordenes.value.length;
  } catch (err) {
    error.value = err.message || 'Error al cargar órdenes';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchOrdenes();
});
</script>

<template>
  <div class="space-y-4">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <p class="text-sm text-n-slate-11 mt-2">Cargando órdenes...</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex flex-col items-center justify-center py-12 gap-4">
      <span class="i-lucide-alert-circle text-6xl text-n-ruby-11" />
      <p class="text-n-slate-11">{{ error }}</p>
    </div>

    <!-- Empty -->
    <div v-else-if="ordenes.length === 0" class="flex flex-col items-center justify-center py-12 gap-3">
      <span class="i-lucide-clipboard-list text-6xl text-n-slate-10" />
      <p class="text-n-slate-11">No hay órdenes para este contrato</p>
    </div>

    <!-- Lista -->
    <div v-else class="space-y-4">
      <!-- Header -->
      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3 class="text-lg font-semibold text-n-slate-12 flex items-center gap-2">
          <span class="i-lucide-clipboard-list" />
          Órdenes del Contrato
        </h3>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ totalCount }} orden(es) encontrada(s)
        </p>
      </div>

      <!-- Tabla -->
      <div class="bg-n-background border border-n-weak rounded-lg overflow-hidden">
        <table class="w-full">
          <thead class="bg-n-solid-3 border-b border-n-weak">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Clase</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">ID AQUACIS</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Tipo</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Ticket</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-n-slate-11 uppercase">Estado</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase">Fecha solicitud</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-n-slate-11 uppercase"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="orden in ordenes"
              :key="orden.id"
              class="hover:bg-n-alpha-1 transition-colors cursor-pointer"
              @click="openOrder(orden)"
            >
              <!-- Clase -->
              <td class="px-4 py-3">
                <span
                  class="px-2 py-0.5 text-xs rounded font-mono font-semibold"
                  :class="orden.order_class === 'service_order'
                    ? 'bg-n-brand/10 text-n-blue-text'
                    : 'bg-violet-100 text-violet-800'"
                >
                  {{ orderClassLabel(orden.order_class) }}
                </span>
              </td>
              <!-- ID AQUACIS -->
              <td class="px-4 py-3">
                <span class="font-mono text-sm text-n-slate-12">
                  {{ orden.aquasis_order_id || '-' }}
                </span>
                <span
                  v-if="orden.aquasis_sync_error"
                  class="ml-1 i-lucide-alert-triangle text-n-amber-11"
                  :title="orden.aquasis_sync_error"
                />
              </td>
              <!-- Tipo -->
              <td class="px-4 py-3 text-sm text-n-slate-12 max-w-[200px]">
                <span class="line-clamp-1">{{ orderTypeLabel(orden) }}</span>
              </td>
              <!-- Ticket asociado -->
              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{ orden.ticket?.folio || '-' }}
              </td>
              <!-- Estado -->
              <td class="px-4 py-3 text-center">
                <span
                  class="px-2 py-0.5 text-xs rounded-full font-medium"
                  :class="statusColors[orden.status] || 'bg-n-slate-9/10 text-n-slate-11'"
                >
                  {{ statusLabel(orden.status) }}
                </span>
              </td>
              <!-- Fecha -->
              <td class="px-4 py-3 text-sm text-n-slate-11 whitespace-nowrap">
                {{ formatDate(orden.request_date || orden.created_at) }}
              </td>
              <!-- Arrow -->
              <td class="px-4 py-3 text-center">
                <span class="i-lucide-arrow-right text-n-slate-10" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="totalPages > 1" class="flex items-center justify-between px-2 py-3">
        <span class="text-sm text-n-slate-11">
          Página {{ currentPage }} de {{ totalPages }}
        </span>
        <div class="flex gap-2">
          <button
            class="px-3 py-1 text-sm rounded bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="currentPage === 1"
            @click="fetchOrdenes(currentPage - 1)"
          >
            ‹ Anterior
          </button>
          <button
            class="px-3 py-1 text-sm rounded bg-n-solid-3 text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="currentPage === totalPages"
            @click="fetchOrdenes(currentPage + 1)"
          >
            Siguiente ›
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
